import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Heart, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const PaymentSuccess = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setChecking(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out');
      setChecking(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentStatus(response.data);

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
        setChecking(false);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        setChecking(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status');
      setChecking(false);
    }
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-serif text-[#1C1917] mb-4">Invalid Payment Session</h1>
          <Button
            onClick={() => navigate('/dashboard')}
            className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  if (checking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto text-[#881337] mb-4 animate-spin" size={48} />
          <h2 className="text-2xl font-serif text-[#1C1917] mb-2">Verifying Payment...</h2>
          <p className="text-[#57534E]">Please wait while we confirm your payment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      <div className="max-w-2xl mx-auto px-6 py-24">
        {paymentStatus?.payment_status === 'paid' ? (
          <div data-testid="payment-success-message" className="text-center">
            <div className="mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#84A98C]/20 mb-6">
                <CheckCircle className="text-[#84A98C]" size={48} fill="#84A98C" />
              </div>
              <h1 className="text-4xl font-serif font-light tracking-tight text-[#1C1917] mb-4">
                Payment Successful!
              </h1>
              <p className="text-xl text-[#57534E] mb-8">
                Thank you for upgrading to {paymentStatus.metadata?.package_id || 'Premium'}!
              </p>
            </div>

            <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-8 border border-white/50 mb-8">
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-stone-200">
                  <span className="text-[#57534E]">Amount Paid</span>
                  <span className="text-xl font-serif text-[#1C1917]">
                    ${(paymentStatus.amount_total / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-stone-200">
                  <span className="text-[#57534E]">Status</span>
                  <span className="text-[#84A98C] font-medium capitalize">{paymentStatus.payment_status}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#57534E]">Session ID</span>
                  <span className="text-xs text-[#A8A29E] font-mono">{sessionId.substring(0, 20)}...</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-[#57534E]">
                Your subscription has been activated. You now have access to all premium features!
              </p>
              <Button
                data-testid="go-to-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white text-lg"
              >
                Go to Dashboard
              </Button>
            </div>

            <div className="mt-8 pt-8 border-t border-stone-200">
              <div className="flex items-center justify-center gap-2">
                <Heart className="text-[#881337]" size={20} fill="#881337" />
                <span className="text-sm text-[#57534E]">Thank you for choosing VowFlow</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <h1 className="text-3xl font-serif text-[#1C1917] mb-4">Payment Processing</h1>
            <p className="text-[#57534E] mb-8">We're still processing your payment. Please check back in a moment.</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="rounded-full bg-[#881337] hover:bg-[#9F1239] text-white"
            >
              Go to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;